import { useState, useEffect, useMemo } from 'react';

// Standard breakpoints matching Tailwind CSS
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

type BreakpointKey = keyof typeof BREAKPOINTS;

export function useIsMobile(breakpoint: number = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    // Initial check for SSR compatibility
    if (typeof window !== 'undefined') {
      return window.innerWidth < breakpoint;
    }
    return false;
  });

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    // Check on mount
    checkIsMobile();

    // Add event listener
    window.addEventListener('resize', checkIsMobile);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, [breakpoint]);

  return isMobile;
}

// Hook for responsive design with multiple breakpoints
export function useBreakpoint() {
  const [width, setWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return 1024; // Default to desktop
  });

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const breakpoint = useMemo(() => {
    if (width < BREAKPOINTS.sm) return 'xs';
    if (width < BREAKPOINTS.md) return 'sm';
    if (width < BREAKPOINTS.lg) return 'md';
    if (width < BREAKPOINTS.xl) return 'lg';
    if (width < BREAKPOINTS['2xl']) return 'xl';
    return '2xl';
  }, [width]);

  return {
    width,
    breakpoint,
    isMobile: width < BREAKPOINTS.md,
    isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isDesktop: width >= BREAKPOINTS.lg,
    isSmall: width < BREAKPOINTS.sm,
    isMedium: width >= BREAKPOINTS.md && width < BREAKPOINTS.xl,
    isLarge: width >= BREAKPOINTS.xl,
    // Utility functions
    isAbove: (bp: BreakpointKey) => width >= BREAKPOINTS[bp],
    isBelow: (bp: BreakpointKey) => width < BREAKPOINTS[bp],
  };
}

// Hook for detecting touch devices
export function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    const checkTouch = () => {
      setIsTouch(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0
      );
    };

    checkTouch();
  }, []);

  return isTouch;
}

// Hook for responsive values based on breakpoint
export function useResponsiveValue<T>(values: {
  base: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
  '2xl'?: T;
}): T {
  const { width } = useBreakpoint();

  return useMemo(() => {
    if (width >= BREAKPOINTS['2xl'] && values['2xl'] !== undefined) return values['2xl'];
    if (width >= BREAKPOINTS.xl && values.xl !== undefined) return values.xl;
    if (width >= BREAKPOINTS.lg && values.lg !== undefined) return values.lg;
    if (width >= BREAKPOINTS.md && values.md !== undefined) return values.md;
    if (width >= BREAKPOINTS.sm && values.sm !== undefined) return values.sm;
    return values.base;
  }, [width, values]);
}
